import { useEffect, useState } from 'react';

import BpkSkipLink from '@skyscanner-internal/backpack-web/bpk-component-skip-link';
import BpkThemeProvider from '@skyscanner-internal/backpack-web/bpk-theming';
import StringsPropVerification from '@web-platform/global-components-higher-order-components/StringsPropVerification';
import { trackEventFn as defaultTrackEventFn } from '@web-platform/global-components-utils/tracking';
import classnames from 'classnames';
import { redirect as saddlebagRedirect } from 'saddlebag-browser';
import observer from 'saddlebag-observer';

import Logo from './Logo';
import PrimaryNav from './PrimaryNav';
import SecondaryNav from './SecondaryNav';
import { DEFAULT } from './constants';

import type {
  CultureSelectorEmbeddedProps,
  LoginFormEmbeddedProps,
  RedirectFunc,
  SearchContext,
  Strings,
  Urls,
} from './types';
import type { NavigationTrackingComponent } from './userTrackingEvents';
import type { OnCultureSelectorButtonClick } from '@web-platform/global-components-culture-selector/helpers/useCultureSelectorData';
import type { Logger } from '@web-platform/global-components-types/utils';
import type { TrackingEventFn } from '@web-platform/global-components-utils/tracking';

import STYLES from './Header.module.scss';

/**
 * Strings required by the Header component. This does not include optional strings.
 */
const stringsKeys = [
  'carHire',
  'flights',
  'helpAnchorText',
  'hotels',
  'login',
  'skipToResults',
  'userAccount',
  'savedFlights',
  'userPreferences',
] as const;

/**
 * Props for the Header component.
 * @interface
 * @public
 */
export interface HeaderProps {
  /**
   * The currently active tab. ('airli', 'skhot', 'carhi', 'packa')
   * @defaultValue 'airli'
   */
  activeTab?: string;
  /**
   * Props for CultureSelector used inside the Header. Some inner props are omitted from the full cultureSelectorProps type.
   * This prop is optional to allow skipping the render of culture-selector when rendering header server side, these props are *required* in the client
   */
  cultureSelectorProps?: CultureSelectorEmbeddedProps;
  /**
   * Pre-selected currency in the modal.
   * @defaultValue 'GBP'
   */
  currency?: string;
  /**
   * Pre-selected currency in the modal (Must be in sync with currency).
   * @defaultValue '£ GBP'
   */
  currencyLabel?: string;
  /**
   * Function to fetch profile picture URL.
   * @defaultValue `() => null`
   */
  getProfilePicture?: () => string | null;
  /**
   * A boolean determining if the environment is production.
   * @defaultValue false
   */
  isProductionEnv: boolean;
  /**
   * Pre-selected locale in the modal.
   * @defaultValue 'en-gb'
   */
  locale?: string;
  /**
   * Pre-selected locale in the modal (Must be in sync with locale).
   * @defaultValue 'English (UK)'
   */
  localeName?: string;
  /**
   * Logger object
   */
  logger: Logger;

  /**
   * Pre-selected market in the modal.
   * @defaultValue 'uk'
   */
  market?: string;
  /**
   * Pre-selected market in the modal (Must be in sync with market).
   * @defaultValue 'United Kingdom'
   */
  marketName?: string;
  /**
   * Function to handle redirects. Used for logic of whether or not to redirect on login button.
   */
  redirect?: RedirectFunc;
  /**
   * If false, skips showing the culture-sector button.
   * @defaultValue true
   */
  showCultureSelectorButton?: boolean;
  /**
   * If true, displays the secondary navigation.
   * @defaultValue true
   */
  showSecondaryNav?: boolean;
  /**
   * If false, will not display the primary navigation tabs.
   * @defaultValue true
   */
  showTabs?: boolean;
  /**
   * Href for skip link: Accessibility feature to skip straight to content marked by the anchor tag
   */
  skipLinkHref?: string;
  /**
   * Translated strings for the component
   */
  strings: Strings;
  /**
   * Function to track events. If not provided, `defaultTrackEventFn` will be used.
   */
  trackEventFn?: TrackingEventFn;
  /**
   * URLs for the tab items.
   * @defaultValue DEFAULT.URLS exported from './constants'
   */
  urls?: Urls;
  /**
   * If true, uses custom theming.
   * @defaultValue false
   */
  useCustomTheme?: boolean;
  /**
   * If true, maximises header to edges of screen.
   * @defaultValue false
   */
  useMaxWidth?: boolean;
  /**
   * Promise determining if the user is logged in.
   * @defaultValue `Promise.resolve(false)`
   */
  userLoggedIn?: Promise<boolean> | Promise<undefined>;
  onCultureSelectorButtonClick?: OnCultureSelectorButtonClick;
  isFetchingCultureSelectorData?: boolean;
  /**
   * If true, opens tabs in new browser tab.
   * @defaultValue false
   */
  unstable__OpenHeaderTabsInNewBrowserTab?: boolean;
  /** If true, shows the packages tab in the header together with flights, hotels and car hire
   * @defaultValue false
   */
  showPackagesTab?: boolean;
  /**
   * An object of values required for rendering Login Form GC in the Header when the Secondary Navigation is displayed.
   * When the `showSecondaryNav` is true, this prop should be given.
   * @defaultValue `undefined`
   */
  loginFormProps?: LoginFormEmbeddedProps;
  /**
   * Visual change styles experiment version for packages tab.
   */
  unstable__packagesEnableVisualChange?: boolean;
  /**
   * New bubble styles experiment version for packages tab.
   */
  unstable__newBubble?: boolean;
  /**
   * New bubble styles experiment version for hotels tab.
   */
  unstable__hotelsNewBubble?: boolean;
  /**
   * Variants of the Login Button replacing the gravatar on mWeb when logged out only
   * @defaultValue 'A' (control - no change)
   */
  unstable__loginButtonVariant?: string;
  /**
   * MBD component name used for primary navigation impressions and interactions.
   * @defaultValue 'global_navigation'
   */
  trackingComponentName?: NavigationTrackingComponent;
}

const theme = {
  primaryColor: '#ED1B28',
  headerTabBackgroundColor: '#ED1B28',
  headerTabActiveBackgroundColor: '#B1121C',
  privateButtonColourTextSecondary: '#ED1B28',
  privateButtonColourBgSecondary: 'rgb(255, 255, 255)',
  privateButtonColourBgSecondaryPressed: 'rgb(255, 255, 255)',
  linkColor: '#F28494',
  linkHoverColor: '#ED1B28',
  linkActiveColor: '#D11622',
  linkVisitedColor: '#B1121C',
};

/**
 * The `Header` component contains the primary and secondary navigation as well as buttons to the login and culture-selector components.
 * The Header also subscribes to `flights-search-controls-changed` event to update related data based on the user's search context.
 *
 * Use the `getTabUrls` data helper to feed the `urls` prop. This function requires a `generateUrl` function to be passed in.
 * This will be the call to UMS.
 *
 * @example
 * @public
 * ```tsx
 * import Header from '@skyscanner-internal/global-component/header';
 * import getTabUrls from '@skyscanner-internal/global-component/header/data'
 *
 * const headerUrls = await getTabUrls(
 *  generateUrl,
 *  culture.locale,
 *  culture.market
 * );
 *
 * const Layout = () => {
 *   return <>
 *     <Header
 *       activeTab="flights"
 *       currency="USD"
 *       locale="en-us"
 *       showTabs={true}
 *       urls=headerUrls
 *       // ... other props
 *     />
 *   </>
 * }
 * ```
 */
const Header = ({
  activeTab = 'airli',
  cultureSelectorProps,
  currency = 'GBP',
  currencyLabel = '£ GBP',
  getProfilePicture = () => null,
  isFetchingCultureSelectorData,
  isProductionEnv = false,
  locale = 'en-GB',
  localeName = 'English (UK)',
  logger,
  loginFormProps,
  market = 'UK',
  marketName = 'United Kingdom',
  onCultureSelectorButtonClick = () => Promise.resolve(),
  redirect = saddlebagRedirect,
  showCultureSelectorButton = true,
  showPackagesTab = false,
  showSecondaryNav = true,
  showTabs = true,
  skipLinkHref = '',
  strings,
  trackEventFn = defaultTrackEventFn,
  trackingComponentName = 'global_navigation',
  // eslint-disable-next-line camelcase
  unstable__OpenHeaderTabsInNewBrowserTab = false,
  // eslint-disable-next-line camelcase
  unstable__hotelsNewBubble = false,
  // eslint-disable-next-line camelcase
  unstable__loginButtonVariant = 'A',
  // eslint-disable-next-line camelcase
  unstable__newBubble = false,
  // eslint-disable-next-line camelcase
  unstable__packagesEnableVisualChange = false,
  urls = DEFAULT.URLS,
  useCustomTheme = false,
  useMaxWidth = false,
  userLoggedIn = Promise.resolve(false),
}: HeaderProps) => {
  const [searchContextState, setSearchContextState] = useState<SearchContext | undefined>();
  const [searchContextSubscribeHandle, setSearchContextSubscribeHandle] = useState();

  useEffect(() => {
    if (!searchContextSubscribeHandle) {
      // TODO: WOODPECKER-4189: Suppressed for owners to look at while upgrading to eslint-plugin-react-hooks v7
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSearchContextSubscribeHandle(
        observer.subscribe('flights-search-controls-changed', (data: SearchContext) => {
          // To update the URLs on the hotel and car-hire tabs after changing the destination in flights-search-controls
          setSearchContextState(data);
        }),
      );
    }

    return () => {
      observer.unsubscribe(searchContextSubscribeHandle);
    };
  }, [searchContextSubscribeHandle, setSearchContextSubscribeHandle]);

  return (
    <StringsPropVerification
      stringsKeys={stringsKeys}
      componentName="Header"
      strings={strings}
      isProductionEnv={isProductionEnv}
      logger={logger}
    >
      <BpkThemeProvider
        // In theory the unified header is not allowed to override the theme. This is a feature that extends from the perilous header.
        theme={useCustomTheme ? theme : null}
        themeAttributes={Object.keys(theme)}
        component="header"
        className={STYLES.Header}
      >
        <div
          className={classnames(STYLES.Header__wrapper, 'clearfix', {
            [STYLES['Header__wrapper--set-width']]: !useMaxWidth,
            // flights day view and car hire results use wrap class to style header, remove when no longer
            [`wrap`]: useMaxWidth,
          })}
        >
          <div className={STYLES['Header__logo-secondary-nav-wrapper']}>
            <div id="identity" className={STYLES['Header__logo-wrapper']}>
              <Logo
                hiddenText={strings.ariaHome}
                market={market}
                logger={logger}
                trackEventFn={trackEventFn}
              />
            </div>
            {skipLinkHref ? (
              <div id="skip-link" className={STYLES['Header__skip-link']}>
                <BpkSkipLink href={skipLinkHref} label={strings.skipToResults} />
              </div>
            ) : null}
            {showSecondaryNav ? (
              <SecondaryNav
                activeTab={activeTab}
                strings={strings}
                market={market}
                currency={currency}
                currencyLabel={currencyLabel}
                locale={locale}
                localeName={localeName}
                logger={logger}
                marketName={marketName}
                userLoggedIn={userLoggedIn}
                redirect={redirect}
                showCultureSelectorButton={showCultureSelectorButton}
                onCultureSelectorButtonClick={onCultureSelectorButtonClick}
                isFetchingCultureSelectorData={isFetchingCultureSelectorData}
                cultureSelectorProps={cultureSelectorProps}
                getProfilePicture={getProfilePicture}
                trackEventFn={trackEventFn}
                loginFormProps={loginFormProps}
                // eslint-disable-next-line camelcase
                loginButtonVariant={unstable__loginButtonVariant}
              />
            ) : null}
          </div>
          <div
            className={classnames(STYLES['Header__primary-nav-wrapper'], {
              [STYLES['Header__primary-nav-wrapper--noTabs']]: !showTabs,
              [STYLES['Header__primary-nav-wrapper--unstable-new-bubble']]:
                // eslint-disable-next-line camelcase
                unstable__hotelsNewBubble || (showPackagesTab && unstable__newBubble),
            })}
          >
            {' '}
            {showTabs ? (
              <PrimaryNav
                activeTab={activeTab}
                strings={strings}
                urls={urls}
                searchContext={searchContextState}
                logger={logger}
                trackEventFn={trackEventFn}
                // eslint-disable-next-line camelcase
                isOpeningNewTab={unstable__OpenHeaderTabsInNewBrowserTab}
                showPackagesTab={showPackagesTab}
                // eslint-disable-next-line camelcase
                unstable__packagesEnableVisualChange={unstable__packagesEnableVisualChange}
                // eslint-disable-next-line camelcase
                unstable__newBubble={unstable__newBubble}
                // eslint-disable-next-line camelcase
                unstable__hotelsNewBubble={unstable__hotelsNewBubble}
                trackingComponentName={trackingComponentName}
              />
            ) : null}
          </div>
        </div>
      </BpkThemeProvider>
    </StringsPropVerification>
  );
};

export default Header;
